import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import AuthImage from '@/components/AuthImage';
import { getObturateurAttenuationLabel, getObturateurIllustration, getObturateurFormeLabel } from './orderFormConstants';
import { Button } from '@/components/ui/button';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Ear, Plus, SignOut, Package, Clock,
  CheckCircle, ArrowClockwise, XCircle, PencilSimple,
  DownloadSimple, Trash, ShieldCheck, Wrench, Gift,
  CopySimple, PencilLine, User, Eye, EyeSlash, Printer, Cube, Warning
} from '@phosphor-icons/react';
import SavRequestForm from './SavRequestForm';
import ClientSavList from './ClientSavList';

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [pendingVal3dByOrder, setPendingVal3dByOrder] = useState({});
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDraftDialogOpen, setDeleteDraftDialogOpen] = useState(false);
  const [deleteDraftId, setDeleteDraftId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [savDialogOpen, setSavDialogOpen] = useState(false);
  const [savOrder, setSavOrder] = useState(null);
  const [promoStatus, setPromoStatus] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountName, setAccountName] = useState(user?.name || '');
  const [accountCurrentPwd, setAccountCurrentPwd] = useState('');
  const [accountNewPwd, setAccountNewPwd] = useState('');
  const [accountSaving, setAccountSaving] = useState(false);
  const [showAccountPwd, setShowAccountPwd] = useState(false);
  const [savTrackingOpen, setSavTrackingOpen] = useState(false);
  const [savTrackingData, setSavTrackingData] = useState(null);
  const [savTrackingStl, setSavTrackingStl] = useState([]);
  const [savTrackingLoading, setSavTrackingLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [impressionLoading, setImpressionLoading] = useState(null);
  const [savManagerOpen, setSavManagerOpen] = useState(false);
  const [savManagerList, setSavManagerList] = useState([]);
  const [savManagerLoading, setSavManagerLoading] = useState(false);
  const [savManagerMode, setSavManagerMode] = useState('list'); // 'list' | 'pick-order'

  useEffect(() => {
    fetchOrders();
    fetchPromoStatus();
  }, []);

  const fetchPromoStatus = async () => {
    try {
      const res = await axios.get(`${API}/promo/status`);
      if (res.data.has_promo) setPromoStatus(res.data);
    } catch { /* silent */ }
  };

  const fetchOrders = async () => {
    try {
      // Toutes les pages : les commandes terminées (SAV, stats) ne doivent pas être tronquées par la pagination serveur
      const first = await axios.get(`${API}/orders`, { params: { per_page: 200, page: 1 } });
      let list = first.data.orders || first.data;
      const totalPages = first.data.pagination?.total_pages || 1;
      for (let p = 2; p <= totalPages; p++) {
        const r = await axios.get(`${API}/orders`, { params: { per_page: 200, page: p } });
        list = list.concat(r.data.orders || []);
      }
      setOrders(list);

      // For each order that requested 3D validation, fetch the latest active file (if any)
      const want3d = (list || []).filter(o => o.wants_3d_validation && o.status !== 'completed' && o.status !== 'cancelled');
      if (want3d.length > 0) {
        const results = await Promise.all(want3d.map(async (o) => {
          try {
            const r = await axios.get(`${API}/orders/${o.id}/validation3d`);
            const files = (r.data || []).filter(f => f.token && f.status !== 'revoked' && f.status !== 'validated');
            return [o.id, files];
          } catch { return [o.id, []]; }
        }));
        const map = {};
        results.forEach(([oid, files]) => { if (files.length > 0) map[oid] = files; });
        setPendingVal3dByOrder(map);
      } else {
        setPendingVal3dByOrder({});
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSaveAccount = async () => {
    setAccountSaving(true);
    try {
      const payload = { name: accountName };
      if (accountNewPwd) {
        payload.current_password = accountCurrentPwd;
        payload.new_password = accountNewPwd;
      }
      await axios.put(`${API}/auth/account`, payload);
      toast.success('Compte mis a jour');
      setAccountCurrentPwd('');
      setAccountNewPwd('');
      setAccountOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setAccountSaving(false); }
  };

  const openSavManager = async () => {
    setSavManagerOpen(true);
    setSavManagerMode('list');
    setSavManagerLoading(true);
    try {
      const res = await axios.get(`${API}/sav/mine`);
      setSavManagerList(res.data || []);
    } catch {
      toast.error('Erreur chargement des SAV');
    } finally {
      setSavManagerLoading(false);
    }
  };

  // Open tracking dialog directly from SAV item in the manager
  const openSavTrackingFromItem = async (sav) => {
    setSavManagerOpen(false);
    setSavTrackingLoading(true);
    setSavTrackingOpen(true);
    setSavTrackingStl([]);
    try {
      const res = await axios.get(`${API}/sav/${sav.id}`);
      setSavTrackingData(res.data);
      // Also fetch STL files from linked refab order and finalization
      const refabId = res.data?.refabrication_order_id;
      let stl = [];
      if (refabId) {
        try {
          const v = await axios.get(`${API}/validation3d/order/${refabId}`);
          stl = v.data?.files || v.data || [];
        } catch { /* ignore */ }
      }
      const finStl = res.data?.finalization_stl_files || [];
      setSavTrackingStl([...stl, ...finStl]);
    } catch {
      toast.error('Erreur chargement SAV');
    } finally {
      setSavTrackingLoading(false);
    }
  };

  const pickOrderForNewSav = (order) => {
    setSavManagerOpen(false);
    setSavOrder(order);
    setSavDialogOpen(true);
  };



  const openSavTracking = async (order) => {
    setSavTrackingLoading(true);
    setSavTrackingOpen(true);
    setSavTrackingStl([]);
    try {
      const savId = order.sav_id;
      if (savId) {
        const res = await axios.get(`${API}/sav/${savId}`);
        setSavTrackingData(res.data);
      } else {
        setSavTrackingData({ sav_number: order.sav_number || '—', status: 'en_cours', description: 'SAV en cours', created_at: order.created_at });
      }
      // Fetch STL from validation3d for the refab order
      const allStl = [];
      try {
        const v3dRes = await axios.get(`${API}/orders/${order.id}/validation3d`);
        if (v3dRes.data?.length) allStl.push(...v3dRes.data);
      } catch { /* no access or no files */ }
      // Also fetch from refabrication_order_id on SAV if different
      if (savId) {
        try {
          const savRes = await axios.get(`${API}/sav/${savId}`);
          const refabId = savRes.data?.refabrication_order_id;
          if (refabId && refabId !== order.id) {
            try {
              const v3dRes2 = await axios.get(`${API}/orders/${refabId}/validation3d`);
              if (v3dRes2.data?.length) {
                const existingIds = new Set(allStl.map(s => s.id));
                v3dRes2.data.forEach(f => { if (!existingIds.has(f.id)) allStl.push(f); });
              }
            } catch { /* no access */ }
          }
          // Also include finalization_stl_files from SAV request
          const finStl = savRes.data?.finalization_stl_files || [];
          finStl.forEach(f => {
            if (!allStl.find(s => s.id === f.id)) {
              allStl.push({ ...f, version: f.version || 0, status: 'finalized', original_filename: f.filename });
            }
          });
        } catch { /* already fetched */ }
      }
      setSavTrackingStl(allStl);
    } catch (e) {
      toast.error('Erreur chargement SAV');
    } finally { setSavTrackingLoading(false); }
  };

  const [savCommentText, setSavCommentText] = useState('');
  const [savCommentSending, setSavCommentSending] = useState(false);
  const [savCancelOpen, setSavCancelOpen] = useState(false);
  const [savCancelReason, setSavCancelReason] = useState('');
  const [savCancelLoading, setSavCancelLoading] = useState(false);

  const handleSavCancel = async () => {
    if (!savTrackingData?.id) return;
    setSavCancelLoading(true);
    try {
      await axios.post(`${API}/sav/${savTrackingData.id}/cancel`, { reason: savCancelReason });
      toast.success('SAV annulée');
      setSavCancelOpen(false);
      setSavTrackingOpen(false);
      setSavCancelReason('');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'annuler la SAV");
    } finally { setSavCancelLoading(false); }
  };

  const handleSavComment = async () => {
    if (!savCommentText.trim() || !savTrackingData?.id) return;
    setSavCommentSending(true);
    try {
      await axios.post(`${API}/sav/${savTrackingData.id}/comment`, { comment: savCommentText });
      toast.success('Commentaire envoye');
      setSavCommentText('');
      // Refresh SAV data
      const res = await axios.get(`${API}/sav/${savTrackingData.id}`);
      setSavTrackingData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavCommentSending(false); }
  };

  const askCancel = (orderId) => { setCancelOrderId(orderId); setCancelDialogOpen(true); };

  const openOrderDetail = async (order) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailOrder(order);
    try {
      const res = await axios.get(`${API}/orders/${order.id}`);
      setDetailOrder(res.data);
    } catch {
      /* Fallback to inline order data */
    } finally {
      setDetailLoading(false);
    }
  };

  const viewImpressionStl = async (fileId) => {
    if (!detailOrder?.id) return;
    setImpressionLoading(fileId);
    try {
      const res = await axios.post(`${API}/orders/${detailOrder.id}/stl/${fileId}/viewer-link`);
      const token = res.data?.token;
      if (token) {
        window.open(`/viewer/${token}`, '_blank', 'noopener,noreferrer');
      } else {
        toast.error("Lien de visualisation indisponible");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'ouverture du visualiseur");
    } finally {
      setImpressionLoading(null);
    }
  };

  const openOrderRecap = async (orderId) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/recap`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) toast.error('Popup bloqué. Autorisez les popups.');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Erreur lors de l'ouverture du récapitulatif");
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/rgpd/my-data`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `mes-donnees-hamac-${new Date().toISOString().split('T')[0]}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Données exportées avec succès');
    } catch { toast.error("Erreur lors de l'export"); }
    finally { setExporting(false); }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/rgpd/my-account`);
      toast.success('Compte supprimé. Vos données ont été anonymisées.');
      logout(); navigate('/login');
    } catch { toast.error("Erreur lors de la suppression"); }
  };
  const handleCancel = async () => {
    if (!cancelOrderId) return;
    setCancelDialogOpen(false);
    try {
      await axios.post(`${API}/orders/${cancelOrderId}/cancel`);
      toast.success('Commande annulée');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
    setCancelOrderId(null);
  };

  const handleDuplicate = async (orderId) => {
    try {
      const res = await axios.post(`${API}/orders/${orderId}/duplicate`);
      toast.success('Brouillon créé — vous allez être redirigé');
      navigate(`/client/order?draft=${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  const askDeleteDraft = (orderId) => { setDeleteDraftId(orderId); setDeleteDraftDialogOpen(true); };
  const handleDeleteDraft = async () => {
    if (!deleteDraftId) return;
    setDeleteDraftDialogOpen(false);
    try {
      await axios.delete(`${API}/orders/${deleteDraftId}`);
      toast.success('Brouillon supprimé');
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
    setDeleteDraftId(null);
  };

  const getStatusBadge = (status, orderType) => {
    if (orderType?.includes('refabrication')) {
      return <span className="badge-refabrication">Refabrication</span>;
    }
    
    switch (status) {
      case 'draft':
        return <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-sm bg-zinc-200 text-zinc-700 border border-zinc-300">Brouillon</span>;
      case 'pending':
        return <span className="badge-pending">En attente</span>;
      case 'design':
        return <span className="badge-design">Design</span>;
      case 'in_progress':
        return <span className="badge-in-progress">Fabrication</span>;
      case 'completed':
        return <span className="badge-completed">Terminée</span>;
      case 'cancelled':
        return <span className="badge-cancelled">Annulée</span>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const stats = {
    pending: orders.filter(o => o.status === 'pending' || o.status === 'design').length,
    inProgress: orders.filter(o => o.status === 'in_progress').length,
    completed: orders.filter(o => o.status === 'completed').length,
    drafts: orders.filter(o => o.status === 'draft').length
  };

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="client-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Ear size={24} weight="duotone" className="text-zinc-900 sm:w-7 sm:h-7" />
            <span className="font-heading font-bold text-base sm:text-lg tracking-tight">HAMAC</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/client" className="text-zinc-900 font-medium text-sm hover:text-zinc-600 transition-colors">Commandes entreprise</Link>
            <Link to="/client/order" className="text-zinc-600 text-sm hover:text-zinc-900 transition-colors">Nouvelle commande</Link>
            <button type="button" onClick={openSavManager} className="text-zinc-600 text-sm hover:text-amber-700 transition-colors flex items-center gap-1" data-testid="sav-header-btn">
              <Wrench size={14} /> Demande de SAV
            </button>
            <Link to="/client/catalog" className="text-zinc-600 text-sm hover:text-zinc-900 transition-colors">Catalogue</Link>
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-xs sm:text-sm text-zinc-600 hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={() => setAccountOpen(true)} className="text-zinc-600 hover:text-zinc-900" data-testid="account-btn"><User size={18} /></Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-zinc-600 hover:text-zinc-900" data-testid="logout-btn"><SignOut size={18} /></Button>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="flex md:hidden overflow-x-auto border-t border-zinc-100 px-4">
          <Link to="/client" className="text-zinc-900 font-medium text-xs py-2 px-3 whitespace-nowrap border-b-2 border-zinc-900">Commandes</Link>
          <Link to="/client/order" className="text-zinc-500 text-xs py-2 px-3 whitespace-nowrap">Nouvelle</Link>
          <button type="button" onClick={openSavManager} className="text-amber-600 text-xs py-2 px-3 whitespace-nowrap flex items-center gap-1" data-testid="sav-header-btn-mobile">
            <Wrench size={12} />SAV
          </button>
          <Link to="/client/catalog" className="text-zinc-500 text-xs py-2 px-3 whitespace-nowrap">Catalogue</Link>
        </div>
      </header>
      {!(user?.establishment_ids?.length) && (
        <div className="bg-amber-50 border-b border-amber-200" data-testid="no-establishment-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 text-xs sm:text-sm text-amber-800 flex items-center gap-2">
            <Warning size={16} className="flex-shrink-0" />
            <span>Votre compte n'est associé à aucun établissement : vous ne pouvez ni consulter ni passer de commande. Contactez HAMAC pour activer votre accès.</span>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Welcome section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 mb-1 sm:mb-2">
            Bonjour, {user?.name}
          </h1>
          <p className="text-zinc-600 text-sm sm:text-base">
            {user?.company_name && `${user.company_name} • `}
            Gérez vos commandes d'embouts sur mesure
          </p>
        </div>

        {/* Promo banner */}
        {promoStatus && promoStatus.promo_status === 'active' && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-sm p-4 sm:p-5" data-testid="promo-banner">
            <div className="flex items-start gap-3">
              <Gift size={24} weight="duotone" className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-heading font-semibold text-sm text-emerald-900 mb-1">Offre promotionnelle active</p>
                <p className="text-sm text-emerald-800">
                  Il vous reste <strong>{(promoStatus.promo_quota_total || 10) - (promoStatus.promo_quota_used || 0)} embout(s) offert(s)</strong> sur {promoStatus.promo_quota_total || 10}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex-1 bg-emerald-200 rounded-full h-2">
                    <div
                      className="bg-emerald-600 h-2 rounded-full transition-all"
                      style={{ width: `${((promoStatus.promo_quota_used || 0) / (promoStatus.promo_quota_total || 10)) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-emerald-700 font-medium whitespace-nowrap">
                    {promoStatus.promo_quota_used || 0}/{promoStatus.promo_quota_total || 10} utilisés
                  </span>
                </div>
                {promoStatus.promo_end_date && (
                  <p className="text-xs text-emerald-600 mt-2">
                    Valable jusqu'au {new Date(promoStatus.promo_end_date).toLocaleDateString('fr-FR')}
                    {' '}({Math.max(0, Math.ceil((new Date(promoStatus.promo_end_date) - new Date()) / (1000 * 60 * 60 * 24)))} jours restants)
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Link to="/client/order" className="bg-zinc-900 text-white p-4 sm:p-6 border border-zinc-800 hover:bg-zinc-800 transition-colors group col-span-2 sm:col-span-1" data-testid="new-order-card">
            <Plus size={24} className="mb-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <p className="label-overline text-zinc-400">Action</p>
            <p className="font-heading font-semibold text-base sm:text-lg">Nouvelle commande</p>
          </Link>
          <div className="bg-white p-4 sm:p-6 border border-zinc-200">
            <Clock size={20} className="mb-2 sm:mb-3 text-rose-600 sm:w-6 sm:h-6" />
            <p className="label-overline">En attente</p>
            <p className="font-heading font-bold text-xl sm:text-2xl">{stats.pending}</p>
          </div>
          <div className="bg-white p-4 sm:p-6 border border-zinc-200">
            <ArrowClockwise size={20} className="mb-2 sm:mb-3 text-amber-500 sm:w-6 sm:h-6" />
            <p className="label-overline">En cours</p>
            <p className="font-heading font-bold text-xl sm:text-2xl">{stats.inProgress}</p>
          </div>
          <div className="bg-white p-4 sm:p-6 border border-zinc-200">
            <CheckCircle size={20} className="mb-2 sm:mb-3 text-emerald-600 sm:w-6 sm:h-6" />
            <p className="label-overline">Terminées</p>
            <p className="font-heading font-bold text-xl sm:text-2xl">{stats.completed}</p>
          </div>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Link 
            to="/client/catalog"
            className="bg-white p-6 border border-zinc-200 hover:border-zinc-400 transition-colors flex items-center gap-4"
          >
            <Package size={24} className="text-zinc-600" />
            <div>
              <p className="font-medium">Catalogue produits</p>
              <p className="text-sm text-zinc-500">Découvrez nos embouts</p>
            </div>
          </Link>
        </div>

        {/* Orders table */}
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <div className="p-4 sm:p-6 border-b border-zinc-200">
            <h2 className="font-heading font-semibold text-base sm:text-lg">Historique des commandes</h2>
          </div>
          
          {loading ? (
            <div className="p-12 text-center text-zinc-500">
              <div className="spinner mx-auto mb-4" />
              Chargement...
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              <Package size={48} className="mx-auto mb-4 text-zinc-300" weight="duotone" />
              <p>Aucune commande pour le moment</p>
              <Link to="/client/order">
                <Button className="mt-4 rounded-sm bg-zinc-900 hover:bg-zinc-800">
                  Créer ma première commande
                </Button>
              </Link>
            </div>
          ) : (
            <Table className="dense-table zebra-table">
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="label-overline">Date</TableHead>
                  <TableHead className="label-overline">Réf. Patient</TableHead>
                  <TableHead className="label-overline hidden sm:table-cell">N° Série</TableHead>
                  <TableHead className="label-overline hidden md:table-cell">Type</TableHead>
                  <TableHead className="label-overline hidden sm:table-cell">Côté</TableHead>
                  <TableHead className="label-overline hidden md:table-cell">Créé par</TableHead>
                  <TableHead className="label-overline hidden sm:table-cell">Statut</TableHead>
                  <TableHead className="label-overline text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow 
                    key={order.id} 
                    className={`table-row-hover ${order.status === 'pending' ? 'order-row-pending' : ''} ${order.order_type?.includes('refabrication') ? 'order-row-refabrication' : ''} ${order.status === 'cancelled' ? 'opacity-60' : ''}`}
                    data-testid={`order-row-${order.id}`}
                  >
                    <TableCell className="text-xs sm:text-sm whitespace-nowrap">{formatDate(order.created_at)}</TableCell>
                    <TableCell className="font-medium text-sm max-w-[120px] sm:max-w-none truncate">
                      {order.patient_reference}
                      {(user?.establishment_ids?.length > 1) && order.establishment_name && (
                        <p className="text-[10px] text-sky-700 font-normal truncate" data-testid={`row-establishment-${order.id}`}>{order.establishment_name}</p>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs sm:text-sm hidden sm:table-cell">
                      {order.serial_number_left && <div>L: {order.serial_number_left}</div>}
                      {order.serial_number_right && <div>R: {order.serial_number_right}</div>}
                    </TableCell>
                    <TableCell className="capitalize text-sm hidden md:table-cell" data-testid={`row-type-${order.id}`}>
                      {(() => {
                        const opts = order.options || {};
                        const typeLabels = {
                          'ecouteur_deporte': 'Écouteur déporté',
                          'bte': 'BTE (contour)',
                          'silk': 'Intra Silk',
                          'integral': 'Intégral',
                        };
                        const typeRaw = order.embout_type || opts.embout_type;
                        const type = typeRaw ? (typeLabels[typeRaw] || typeRaw) : null;
                        const formeRaw = opts.forme_embout || opts.forme_embout_od;
                        // Pour Intra Silk, la forme est unique — pas d'info supplémentaire à afficher
                        const forme = formeRaw && formeRaw !== 'silk' ? formeRaw.replace(/_/g, ' ') : null;
                        if (opts.fabrication_type === 'obturateur') {
                          return <span>Obturateur{opts.obturateur_type ? ` — ${opts.obturateur_type.replace(/_/g, ' ')}` : ''}</span>;
                        }
                        if (opts.fabrication_type === 'conformateur_oreille') {
                          return <span>Conformateur d&apos;oreille</span>;
                        }
                        if (!type && !forme) return <span className="text-zinc-300">—</span>;
                        return (
                          <div className="leading-tight">
                            {type && <p className="font-medium text-xs">{type}</p>}
                            {forme && <p className="text-[11px] text-zinc-500 capitalize">{forme}</p>}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="capitalize text-sm hidden sm:table-cell">
                      {order.ear_side === 'left' ? 'Gauche' : 
                       order.ear_side === 'right' ? 'Droit' : 'Bilatéral'}
                    </TableCell>
                    <TableCell className="text-xs text-zinc-500 hidden md:table-cell">
                      {order.ordered_by_name || order.created_by_name || order.client_name}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{getStatusBadge(order.status, order.order_type)}</TableCell>
                    <TableCell className="text-right p-1 sm:p-2">
                      <div className="flex items-center justify-end gap-0.5 sm:gap-1">
                        {/* SAV refabrication orders: only "Suivi de la SAV" */}
                        {order.is_sav_refabrication ? (
                          <Button variant="ghost" size="sm" onClick={() => openSavTracking(order)}
                            className="h-7 px-1.5 sm:px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            title="Suivi de la SAV" data-testid={`sav-tracking-btn-${order.id}`}>
                            <Wrench size={14} /><span className="text-xs hidden sm:inline ml-1">Suivi SAV</span>
                          </Button>
                        ) : (
                          <>
                        {order.status !== 'draft' && (
                          <Button variant="ghost" size="sm" onClick={() => openOrderDetail(order)}
                            className="h-7 px-1.5 sm:px-2 text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100"
                            title="Voir le récapitulatif" data-testid={`view-order-${order.id}`}>
                            <Eye size={14} /><span className="text-xs hidden sm:inline ml-1">Voir</span>
                          </Button>
                        )}
                        {order.status === 'draft' && (
                          <>
                            <Link to={`/client/order?draft=${order.id}`}>
                              <Button variant="ghost" size="sm" className="h-7 px-1.5 sm:px-2 text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100" title="Reprendre le brouillon" data-testid={`resume-draft-${order.id}`}>
                                <PencilLine size={14} /><span className="text-xs hidden sm:inline ml-1">Reprendre</span>
                              </Button>
                            </Link>
                            <Button variant="ghost" size="sm" onClick={() => askDeleteDraft(order.id)} className="h-7 px-1.5 sm:px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Supprimer le brouillon" data-testid={`delete-draft-${order.id}`}>
                              <Trash size={14} />
                            </Button>
                          </>
                        )}
                        {order.status !== 'draft' && order.status !== 'cancelled' && (
                          <Button variant="ghost" size="sm" onClick={() => handleDuplicate(order.id)} className="h-7 px-1.5 sm:px-2 text-zinc-600 hover:text-zinc-800 hover:bg-zinc-100" title="Dupliquer la commande" data-testid={`duplicate-btn-${order.id}`}>
                            <CopySimple size={14} /><span className="text-xs hidden sm:inline ml-1">Dupliquer</span>
                          </Button>
                        )}
                        {(order.status === 'completed' || order.status === 'shipped') && (
                          <Button variant="ghost" size="sm" onClick={() => { setSavOrder(order); setSavDialogOpen(true); }} className="h-7 px-1.5 sm:px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50" title="Demande SAV" data-testid={`sav-btn-${order.id}`}>
                            <Wrench size={14} /><span className="text-xs hidden sm:inline ml-1">SAV</span>
                          </Button>
                        )}
                        {/* 3D viewer button — visible when there's at least one pending validation 3D file */}
                        {pendingVal3dByOrder[order.id]?.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              // Open the most recent pending file; if both sides exist, open them sequentially via setTimeout
                              const files = pendingVal3dByOrder[order.id];
                              files.forEach((f, i) => {
                                setTimeout(() => window.open(`/viewer/${f.token}`, '_blank', 'noopener,noreferrer'), i * 150);
                              });
                            }}
                            className="h-7 px-1.5 sm:px-2 text-red-600 hover:text-red-700 hover:bg-red-50 animate-pulse"
                            title="Visualiser et valider le modèle 3D avant fabrication"
                            data-testid={`view-3d-${order.id}`}
                          >
                            <Cube size={14} weight="duotone" />
                            <span className="text-xs hidden sm:inline ml-1">Voir 3D</span>
                          </Button>
                        )}

                        {order.status === 'pending' && !(order.stl_files?.length > 0) && (
                          <Link to={`/client/order?edit=${order.id}`}>
                            <Button variant="ghost" size="sm" className="h-7 px-1.5 sm:px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Modifier la commande" data-testid={`edit-order-${order.id}`}>
                              <PencilSimple size={14} /><span className="text-xs hidden sm:inline ml-1">Modifier</span>
                            </Button>
                          </Link>
                        )}
                        {order.status === 'pending' && (
                          <Button variant="ghost" size="sm" onClick={() => askCancel(order.id)} className="h-7 px-1.5 sm:px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Annuler la commande" data-testid={`cancel-order-${order.id}`}>
                            <XCircle size={14} /><span className="text-xs hidden sm:inline ml-1">Annuler</span>
                          </Button>
                        )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Client SAV List */}
        <ClientSavList />

        {/* RGPD Section */}
        <div className="mt-8 border border-zinc-200 rounded-sm bg-white p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={20} className="text-zinc-600" />
            <h2 className="font-heading font-semibold text-sm">Mes données personnelles</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={handleExportData} disabled={exporting} data-testid="export-data-btn">
              <DownloadSimple size={14} className="mr-2" />
              {exporting ? 'Export...' : 'Télécharger mes données'}
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-account-btn">
              <Trash size={14} className="mr-2" />
              Supprimer mon compte
            </Button>
            <Link to="/confidentialite" target="_blank" className="inline-flex items-center text-xs text-zinc-500 underline hover:text-zinc-700">
              Politique de confidentialité
            </Link>
          </div>
        </div>
      </main>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer mon compte</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Vos données personnelles seront anonymisées. Vos commandes seront conservées de manière anonyme conformément aux obligations légales.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleDeleteAccount} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-account-btn">
              <Trash size={14} className="mr-1" />Confirmer la suppression
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Annuler la commande</DialogTitle>
            <DialogDescription>Êtes-vous sûr de vouloir annuler cette commande ? Cette action est irréversible.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="rounded-sm">Non, garder</Button>
            <Button onClick={handleCancel} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-cancel-btn">
              <XCircle size={14} className="mr-1" />Oui, annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Draft Dialog */}
      <Dialog open={deleteDraftDialogOpen} onOpenChange={setDeleteDraftDialogOpen}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer le brouillon</DialogTitle>
            <DialogDescription>Êtes-vous sûr de vouloir supprimer ce brouillon ? Cette action est irréversible.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDraftDialogOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleDeleteDraft} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-draft-btn">
              <Trash size={14} className="mr-1" />Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* SAV Manager Dialog (liste des SAV + nouvelle demande) */}
      <Dialog open={savManagerOpen} onOpenChange={setSavManagerOpen}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sav-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench size={20} className="text-amber-600" />
              {savManagerMode === 'pick-order' ? 'Choisir une commande pour le SAV' : 'Mes demandes SAV'}
            </DialogTitle>
            <DialogDescription>
              {savManagerMode === 'pick-order'
                ? 'Selectionnez la commande concernee par le nouveau SAV.'
                : 'Suivez vos SAV en cours, consultez les reponses et visualisez les nouveaux STL.'}
            </DialogDescription>
          </DialogHeader>

          {savManagerMode === 'list' ? (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button
                  onClick={() => setSavManagerMode('pick-order')}
                  className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="sav-manager-new-btn">
                  <Plus size={14} className="mr-1" />Nouvelle demande SAV
                </Button>
              </div>

              {savManagerLoading ? (
                <p className="text-center text-zinc-400 py-8">Chargement...</p>
              ) : savManagerList.length === 0 ? (
                <div className="text-center py-10 border border-dashed border-zinc-200 rounded-sm">
                  <Wrench size={32} className="mx-auto text-zinc-300 mb-2" />
                  <p className="text-sm text-zinc-500">Aucune demande SAV en cours.</p>
                  <p className="text-xs text-zinc-400 mt-1">Cliquez sur "Nouvelle demande SAV" pour en creer une.</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm" data-testid="sav-manager-list">
                  {savManagerList.map(sav => (
                    <button
                      key={sav.id}
                      type="button"
                      onClick={() => openSavTrackingFromItem(sav)}
                      className="w-full p-3 sm:p-4 hover:bg-amber-50 transition-colors text-left"
                      data-testid={`sav-manager-item-${sav.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-mono font-bold text-sm">{sav.sav_number}</span>
                            {sav.urgency === 'urgent' && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-sm">Urgent</span>
                            )}
                            {sav.decision && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm">
                                {sav.decision.type === 'refabrication_gratuite' ? 'Refab gratuite'
                                  : sav.decision.type === 'refabrication_payante' ? 'Refab payante'
                                  : 'Geste commercial'}
                              </span>
                            )}
                            {sav.finalization_stl_files?.length > 0 && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded-sm flex items-center gap-1">
                                <Eye size={10} />Nouveau STL
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-700 truncate">{sav.product_name} {sav.order_patient_ref ? `— ${sav.order_patient_ref}` : ''}</p>
                          <p className="text-xs text-zinc-400 mt-0.5">
                            {(sav.request_type || '').replace(/_/g, ' ')} — {new Date(sav.created_at).toLocaleDateString('fr-FR')}
                            {sav.comments?.length > 0 && <span className="ml-2 text-amber-700">• {sav.comments.length} message{sav.comments.length > 1 ? 's' : ''}</span>}
                          </p>
                        </div>
                        <span className="text-[11px] font-medium px-2 py-1 rounded-sm whitespace-nowrap bg-zinc-100 text-zinc-700 capitalize">
                          {(sav.status || '').replace(/_/g, ' ')}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Button variant="ghost" size="sm" onClick={() => setSavManagerMode('list')} className="rounded-sm text-zinc-500" data-testid="sav-manager-back-btn">
                ← Retour aux SAV
              </Button>
              {orders.filter(o => ['completed', 'shipped'].includes(o.status) && !o.is_sav_refabrication).length === 0 ? (
                <p className="text-center text-sm text-zinc-500 py-8">Aucune commande terminee disponible pour un SAV.</p>
              ) : (
                <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm max-h-96 overflow-y-auto">
                  {orders.filter(o => ['completed', 'shipped'].includes(o.status) && !o.is_sav_refabrication).map(o => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => pickOrderForNewSav(o)}
                      className="w-full p-3 hover:bg-amber-50 transition-colors text-left"
                      data-testid={`sav-manager-pick-order-${o.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{o.product_name} — {o.patient_reference || '—'}</p>
                          <p className="text-xs text-zinc-400 mt-0.5">
                            {o.ear_side === 'left' ? 'Oreille gauche' : o.ear_side === 'right' ? 'Oreille droite' : 'Binaural'}
                            {o.completed_at && ` • Livree le ${new Date(o.completed_at).toLocaleDateString('fr-FR')}`}
                          </p>
                        </div>
                        <Wrench size={16} className="text-amber-600" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SAV Request Dialog */}
      <SavRequestForm
        open={savDialogOpen}
        onOpenChange={setSavDialogOpen}
        order={savOrder}
        onSuccess={fetchOrders}
      />

      {/* SAV Tracking Dialog */}
      <Dialog open={savTrackingOpen} onOpenChange={setSavTrackingOpen}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sav-tracking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wrench size={20} className="text-amber-600" />Suivi de la SAV {savTrackingData?.sav_number}</DialogTitle>
          </DialogHeader>
          {savTrackingLoading ? (
            <div className="p-8 text-center text-zinc-400">Chargement...</div>
          ) : savTrackingData ? (
            <div className="space-y-4">
              {/* General info */}
              <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Informations generales</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-zinc-400 text-xs">Date de demande</span><p className="font-medium">{new Date(savTrackingData.created_at).toLocaleDateString('fr-FR')}</p></div>
                  <div><span className="text-zinc-400 text-xs">Statut</span><p className="font-medium capitalize">{(savTrackingData.status || '').replace(/_/g, ' ')}</p></div>
                  <div><span className="text-zinc-400 text-xs">Motif</span><p>{(savTrackingData.request_type || '').replace(/_/g, ' ')}</p></div>
                  <div><span className="text-zinc-400 text-xs">Produit</span><p>{savTrackingData.product_name || '—'}</p></div>
                </div>
              </div>

              {/* Description */}
              {savTrackingData.description && (
                <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Description du probleme</p>
                  <p className="text-sm bg-zinc-50 p-3 rounded-sm">{savTrackingData.description}</p>
                </div>
              )}

              {/* Decision */}
              {savTrackingData.decision && (
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-sm">
                  <p className="text-xs text-emerald-600 uppercase tracking-wider mb-2">Decision</p>
                  <p className="text-sm font-bold">{(savTrackingData.decision.type || '').replace(/_/g, ' ')}</p>
                  <p className="text-xs text-zinc-600 mt-1">{savTrackingData.decision.justification}</p>
                  {savTrackingData.decision.decided_by_name && (
                    <p className="text-[10px] text-emerald-500 mt-1">Valide par : {savTrackingData.decision.decided_by_name}</p>
                  )}
                </div>
              )}

              {/* STL Files — newest first */}
              {savTrackingStl.length > 0 && (
                <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Fichiers STL ({savTrackingStl.length})</p>
                  <div className="space-y-2">
                    {[...savTrackingStl].sort((a, b) => (b.version || 0) - (a.version || 0)).map((f) => (
                      <div key={f.id} className="flex items-center gap-2 p-2.5 bg-zinc-50 border border-zinc-200 rounded-sm">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium truncate">{f.original_filename || f.filename}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${
                              savTrackingStl.filter(x => x.side === f.side).sort((a, b) => (b.version||0) - (a.version||0))[0]?.id === f.id
                                ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-200 text-zinc-600'
                            }`}>V{f.version || '?'}</span>
                            <span className="text-[10px] text-zinc-400">{f.side === 'left' ? 'OG' : 'OD'}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {(f.created_at || f.uploaded_at) && <span className="text-[10px] text-zinc-400">{new Date(f.created_at || f.uploaded_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                            {(f.created_by || f.uploaded_by) && <span className="text-[10px] text-zinc-400">{f.created_by || f.uploaded_by}</span>}
                          </div>
                        </div>
                        {f.token && (
                          <a href={`${window.location.origin}/viewer/${f.token}`} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-8 text-xs" data-testid={`sav-view-stl-${f.id}`}>
                              <Eye size={14} className="mr-1" />Visualiser
                            </Button>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Réclamation / Commentaires */}
              <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Reclamation / Commentaires</p>

                {/* Existing comments */}
                {savTrackingData.comments?.length > 0 && (
                  <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                    {[...savTrackingData.comments].reverse().map((c) => (
                      <div key={c.id} className={`p-3 rounded-sm text-sm ${c.user_role === 'client' ? 'bg-blue-50 border border-blue-200' : 'bg-zinc-50 border border-zinc-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-xs">{c.user_name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${c.user_role === 'client' ? 'bg-blue-100 text-blue-700' : 'bg-zinc-200 text-zinc-600'}`}>{c.user_role === 'client' ? 'Client' : 'Equipe'}</span>
                          <span className="text-[10px] text-zinc-400 ml-auto">{new Date(c.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-xs text-zinc-700">{c.comment}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* New comment form */}
                <div className="space-y-2">
                  <textarea value={savCommentText} onChange={(e) => setSavCommentText(e.target.value)}
                    placeholder="Ecrivez votre reclamation ou commentaire ici..."
                    className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-20 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    data-testid="sav-comment-input" />
                  <Button onClick={handleSavComment} disabled={savCommentSending || !savCommentText.trim()}
                    className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white w-full" data-testid="sav-comment-send-btn">
                    <PencilSimple size={16} className="mr-2" />
                    {savCommentSending ? 'Envoi...' : 'Envoyer un commentaire'}
                  </Button>
                </div>
              </div>

              {/* Status History */}
              {savTrackingData.status_history?.length > 0 && (
                <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Historique</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {[...savTrackingData.status_history].reverse().map((h) => (
                      <div key={`${h.changed_at}-${h.status}`} className="flex items-start gap-3 text-xs">
                        <span className="text-zinc-400 flex-shrink-0 w-24 font-mono">{new Date(h.changed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        <div>
                          <span className="font-medium capitalize">{(h.status || '').replace(/_/g, ' ')}</span>
                          {h.changed_by_name && <span className="text-zinc-400 ml-2">par {h.changed_by_name}</span>}
                          {h.comment && <p className="text-zinc-500 mt-0.5">{h.comment}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
            {savTrackingData && !['cloturee', 'annulee', 'en_fabrication', 'expediee'].includes(savTrackingData.status) && (
              <Button
                variant="outline"
                onClick={() => setSavCancelOpen(true)}
                className="rounded-sm border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                data-testid="cancel-sav-btn">
                <XCircle size={14} className="mr-1" />Annuler la SAV
              </Button>
            )}
            <Button variant="outline" onClick={() => setSavTrackingOpen(false)} className="rounded-sm">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SAV Cancel Confirmation Dialog */}
      <Dialog open={savCancelOpen} onOpenChange={setSavCancelOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="cancel-sav-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600"><XCircle size={20} />Annuler la SAV {savTrackingData?.sav_number}</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir annuler cette demande de SAV ? Cette action est irréversible.
              La commande de refabrication associée sera également annulée.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">Raison (optionnel)</label>
            <textarea
              value={savCancelReason}
              onChange={(e) => setSavCancelReason(e.target.value)}
              rows={3}
              className="w-full border border-zinc-200 rounded-sm p-2 text-sm focus:outline-none focus:border-zinc-400"
              placeholder="Pourquoi annulez-vous cette SAV ?"
              data-testid="cancel-sav-reason" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSavCancelOpen(false)} className="rounded-sm" disabled={savCancelLoading}>Retour</Button>
            <Button
              onClick={handleSavCancel}
              disabled={savCancelLoading}
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-cancel-sav-btn">
              <XCircle size={14} className="mr-1" />{savCancelLoading ? 'Annulation…' : 'Oui, annuler la SAV'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-white max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={20} className="text-zinc-700" />
              Récapitulatif de la commande
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="p-8 text-center text-zinc-400">Chargement...</div>
          ) : detailOrder ? (
            <div className="space-y-4">
              {/* General */}
              <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Informations générales</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-zinc-400 text-xs">Date de création</span><p className="font-medium">{formatDate(detailOrder.created_at)}</p></div>
                  <div><span className="text-zinc-400 text-xs">Statut</span><p>{getStatusBadge(detailOrder.status, detailOrder.order_type)}</p></div>
                  <div><span className="text-zinc-400 text-xs">Réf. patient</span><p className="font-medium">{detailOrder.patient_reference}</p></div>
                  {detailOrder.establishment_name && <div><span className="text-zinc-400 text-xs">Établissement</span><p className="font-medium" data-testid="detail-establishment">{detailOrder.establishment_name}</p>{detailOrder.establishment_address && <p className="text-xs text-zinc-500">{detailOrder.establishment_address}</p>}</div>}
                  <div><span className="text-zinc-400 text-xs">Côté</span><p className="font-medium">{detailOrder.ear_side === 'left' ? 'Gauche' : detailOrder.ear_side === 'right' ? 'Droit' : 'Bilatéral'}</p></div>
                  {detailOrder.serial_number_left && <div><span className="text-zinc-400 text-xs">N° série (G)</span><p className="font-mono text-xs">{detailOrder.serial_number_left}</p></div>}
                  {detailOrder.serial_number_right && <div><span className="text-zinc-400 text-xs">N° série (D)</span><p className="font-mono text-xs">{detailOrder.serial_number_right}</p></div>}
                  {detailOrder.delivery_date && <div><span className="text-zinc-400 text-xs">Date de livraison souhaitée</span><p className="font-medium">{formatDate(detailOrder.delivery_date)}</p></div>}
                  {(detailOrder.ordered_by_name || detailOrder.created_by_name) && <div><span className="text-zinc-400 text-xs">Passée par</span><p className="font-medium">{detailOrder.ordered_by_name || detailOrder.created_by_name}</p></div>}
                </div>
              </div>

              {/* Product */}
              <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Produit</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-zinc-400 text-xs">Nom du produit</span><p className="font-medium">{detailOrder.product_name || '—'}</p></div>
                  <div><span className="text-zinc-400 text-xs">Type d'embout</span><p className="font-medium capitalize">{detailOrder.embout_type || '—'}</p></div>
                  <div><span className="text-zinc-400 text-xs">Matériau</span><p className="font-medium capitalize">{detailOrder.material || '—'}</p></div>
                  <div><span className="text-zinc-400 text-xs">Couleur</span><p className="font-medium capitalize">{detailOrder.color || '—'}</p></div>
                </div>
              </div>

              {/* Obturateur — visuel + forme + atténuation (récap client).
                  Fallback image sur le type d'obturateur si la forme n'est pas
                  reconnue ou absente. */}
              {detailOrder.options?.fabrication_type === 'obturateur'
                && (detailOrder.options?.obturateur_forme || detailOrder.options?.obturateur_type) && (() => {
                const illu = getObturateurIllustration(detailOrder.options.obturateur_type, detailOrder.options.obturateur_forme);
                const label = getObturateurFormeLabel(detailOrder.options.obturateur_type, detailOrder.options.obturateur_forme);
                if (!illu && !label && !detailOrder.options.obturateur_attenuation) return null;
                return (
                  <div className="bg-white border border-zinc-200 p-4 rounded-sm" data-testid="detail-obturateur-forme-client">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Forme choisie</p>
                    <div className="flex items-center gap-4">
                      {illu?.image && (
                        <div className="w-24 h-24 bg-zinc-50 border border-zinc-200 rounded-sm overflow-hidden flex-shrink-0">
                          <img
                            src={illu.image}
                            alt={label || 'Obturateur'}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <p className="font-semibold text-sm text-zinc-800 capitalize">
                        {label || '—'}
                      </p>
                    </div>
                    {detailOrder.options.obturateur_attenuation && (
                      <p className="text-xs text-zinc-600 mt-3">
                        <span className="uppercase tracking-wider text-zinc-400">Filtre / Atténuation :</span>{' '}
                        <span className="font-semibold text-zinc-800">{getObturateurAttenuationLabel(detailOrder.options.obturateur_attenuation) || detailOrder.options.obturateur_attenuation}</span>
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Options / Caracteristiques */}
              {detailOrder.options && Object.keys(detailOrder.options).length > 0 && (() => {
                const opts = detailOrder.options || {};
                const isObturateur = opts.fabrication_type === 'obturateur';
                // Fields strictly tied to an obturateur context — hide them on a hearing aid order
                const OBTURATEUR_ONLY_KEYS = new Set([
                  'obturateur_type', 'obturateur_forme', 'obturateur_poignee', 'obturateur_cordon',
                  'obturateur_etui', 'filtre_type', 'filtre_attenuation',
                  'antibruit_non_filtre_durete_strict',
                ]);
                // Fields strictly tied to a hearing aid context — hide them on an obturateur order
                const HEARING_AID_ONLY_KEYS = new Set([
                  'marque', 'modele', 'ecouteur_info', 'ecouteur_version', 'ecouteur_puissance',
                  'ecouteur_puissance_od', 'ecouteur_puissance_og',
                  'ecouteur_puissance_oticon', 'ecouteur_puissance_oticon_od', 'ecouteur_puissance_oticon_og',
                  'integral_cable_length', 'fournir_bushing_cerustop', 'montage_type', 'finition_anallergique',
                  'bte_brand_specific_enabled', 'bte_brand_specific_type',
                ]);
                // Internal / noisy flags that the client should never see in a recap
                const HIDDEN_KEYS = new Set([
                  'reception_email', 'reception_mode', 'envoi_courrier',
                  'patient_type',
                  'forme_demi_epaulement_od', 'forme_demi_epaulement_og',
                  'forme_evidee_od', 'forme_evidee_og',
                  'forme_embout_od', 'forme_embout_og',
                  'matiere_od', 'matiere_og', 'durete_od', 'durete_og',
                  'event_od', 'event_og',
                  'forme_demi_epaulement', 'forme_evidee',
                  'marquage_special',
                  // Conformateur : rendus dans un bloc dédié plus bas
                  'conformateur_description', 'conformateur_photo_ids',
                  // Obturateur : forme rendue dans un bloc dédié avec image
                  'obturateur_forme',
                  // Obturateur : atténuation rendue dans le bloc "Forme choisie"
                  'obturateur_attenuation',
                ]);

                const visible = Object.entries(opts).filter(([k, v]) => {
                  // Drop empties and explicit false-y values that mean "non sélectionné"
                  if (v === null || v === undefined || v === '' || v === false || v === 'false') return false;
                  // Drop "false" booleans regardless of casing
                  if (typeof v === 'string' && v.toLowerCase() === 'false') return false;
                  if (HIDDEN_KEYS.has(k)) return false;
                  if (isObturateur && HEARING_AID_ONLY_KEYS.has(k)) return false;
                  if (!isObturateur && OBTURATEUR_ONLY_KEYS.has(k)) return false;
                  return true;
                });

                if (visible.length === 0) return null;

                return (
                  <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                    <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Caractéristiques</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {visible.map(([k, v]) => (
                        <div key={k}>
                          <span className="text-zinc-400 text-xs capitalize">{k.replace(/_/g, ' ')}</span>
                          <p className="font-medium text-xs break-words">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Conformateur d'oreille — bloc dédié pour rappeler le besoin au client */}
              {detailOrder.options?.fabrication_type === 'conformateur_oreille' && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm" data-testid="detail-conformateur-block-client">
                  <p className="text-xs text-blue-800 uppercase tracking-wider mb-3 font-semibold">Conformateur d&apos;oreille — Votre besoin</p>
                  {detailOrder.options?.conformateur_description ? (
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap mb-3" data-testid="detail-conformateur-description-client">{detailOrder.options.conformateur_description}</p>
                  ) : (
                    <p className="text-xs italic text-zinc-500 mb-3">Aucune description renseignée.</p>
                  )}
                  {Array.isArray(detailOrder.options.conformateur_photo_ids) && detailOrder.options.conformateur_photo_ids.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="detail-conformateur-photos-client">
                      {detailOrder.options.conformateur_photo_ids.map((fid) => (
                        <div key={fid} className="bg-white border border-blue-200 rounded-sm overflow-hidden aspect-square">
                          <AuthImage
                            url={`${API}/orders/conformateur-photo/${fid}`}
                            alt="Photo conformateur"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Selected options */}
              {Array.isArray(detailOrder.selected_options) && detailOrder.selected_options.length > 0 && (
                <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-3">Options sélectionnées</p>
                  <ul className="space-y-1 text-sm">
                    {detailOrder.selected_options.map((opt) => (
                      <li key={opt.id || opt.lookup_key} className="flex items-center justify-between border-b border-zinc-100 pb-1">
                        <span>{opt.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notes */}
              {detailOrder.notes && (
                <div className="bg-white border border-zinc-200 p-4 rounded-sm">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Notes</p>
                  <p className="text-sm bg-zinc-50 p-3 rounded-sm whitespace-pre-wrap">{detailOrder.notes}</p>
                </div>
              )}

              {/* Impression STL files */}
              {Array.isArray(detailOrder.stl_files) && detailOrder.stl_files.length > 0 ? (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-sm" data-testid="impression-stl-section">
                  <p className="text-xs text-blue-700 uppercase tracking-wider mb-3">Empreintes scannées ({detailOrder.stl_files.length})</p>
                  <div className="space-y-2">
                    {[...detailOrder.stl_files].sort((a, b) => {
                      if (a.side !== b.side) return a.side === 'left' ? -1 : 1;
                      return (b.version || 0) - (a.version || 0);
                    }).map((f) => (
                      <div key={f.file_id} className="flex items-center gap-2 p-2.5 bg-white border border-blue-200 rounded-sm">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium truncate">{f.filename}</span>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700">V{f.version || 1}</span>
                            <span className="text-[10px] text-zinc-500">{f.side === 'left' ? 'OG' : 'OD'}</span>
                          </div>
                          {f.uploaded_at && (
                            <span className="text-[10px] text-zinc-400">Déposé le {new Date(f.uploaded_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          )}
                        </div>
                        <Button size="sm" onClick={() => viewImpressionStl(f.file_id)}
                          disabled={impressionLoading === f.file_id}
                          className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-8 text-xs"
                          data-testid={`view-impression-stl-${f.file_id}`}>
                          <Eye size={14} className="mr-1" />
                          {impressionLoading === f.file_id ? 'Ouverture...' : 'Visualiser'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm text-sm text-zinc-500" data-testid="impression-stl-empty">
                  Aucune empreinte scannée n'a encore été déposée par le technicien.
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            {detailOrder && (
              <Button variant="outline" onClick={() => openOrderRecap(detailOrder.id)} className="rounded-sm" data-testid="print-recap-btn">
                <Printer size={14} className="mr-2" />Imprimer le récapitulatif
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailOpen(false)} className="rounded-sm" data-testid="close-order-detail-btn">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mon compte Dialog */}
      <Dialog open={accountOpen} onOpenChange={setAccountOpen}>
        <DialogContent className="bg-white max-w-[90vw] sm:max-w-md" data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><User size={20} />Mon compte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="label-overline mb-1 block">Nom</label>
              <input type="text" value={accountName} onChange={(e) => setAccountName(e.target.value)}
                className="w-full p-2.5 border border-zinc-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                data-testid="account-name-input" />
            </div>
            <div>
              <label className="label-overline mb-1 block">Email</label>
              <p className="text-sm text-zinc-500 bg-zinc-50 p-2.5 rounded-sm">{user?.email}</p>
            </div>
            <div className="pt-2 border-t border-zinc-200">
              <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Changer le mot de passe</p>
              <div className="space-y-2">
                <div className="relative">
                  <input type={showAccountPwd ? 'text' : 'password'} value={accountCurrentPwd}
                    onChange={(e) => setAccountCurrentPwd(e.target.value)}
                    placeholder="Mot de passe actuel"
                    className="w-full p-2.5 pr-10 border border-zinc-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                    data-testid="account-current-pwd" />
                  <button type="button" onClick={() => setShowAccountPwd(!showAccountPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                    {showAccountPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <input type={showAccountPwd ? 'text' : 'password'} value={accountNewPwd}
                  onChange={(e) => setAccountNewPwd(e.target.value)}
                  placeholder="Nouveau mot de passe"
                  className="w-full p-2.5 border border-zinc-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  data-testid="account-new-pwd" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccountOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={handleSaveAccount} disabled={accountSaving} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="account-save-btn">
              {accountSaving ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
